import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { APIService } from '../../services/api/api-service';
import * as OrderActions from './orders.actions';
import * as NotificationActions from '../notification/notification.actions';
import { switchMap, tap, map, catchError, retry, delay } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { AppState } from '../app.states';
import { Store } from '@ngrx/store';
import { NzModalService } from 'ng-zorro-antd/modal';
import { OrderPayModalComponent } from '../../pages/orders/order-pay-modal/order-pay-modal.component';
import { NotificationBarType } from '@care-portals/api-interfaces';

@Injectable()
export class OrdersEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly notification: NzNotificationService,
    private readonly api: APIService,
    private readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly modalService: NzModalService
  ) {}

  /* -------------------------------------------------------------------------- */
  /*                      Pay invoice with payment method                       */
  /* -------------------------------------------------------------------------- */
  payInvoiceWithPaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.payInvoiceWithPaymentMethod),
      switchMap(action =>
        this.api
          .payInvoiceWithPaymentMethod({
            invoiceId: action.invoiceId,
            paymentMethodId: action.paymentMethodId
          })
          .pipe(
            tap(() =>
              this.notification.success(
                'Payment Success',
                'Your order has been paid successfully'
              )
            ),
            switchMap(invoice => [
              OrderActions.payInvoiceWithPaymentMethodSuccess(invoice),
              OrderActions.closeOrderPayModal()
            ]),
            catchError(error => {
              this.notification.error(
                'Error Paying Invoice With Payment Method',
                error.message
              );
              return of(OrderActions.payInvoiceWithPaymentMethodFailure(error));
            })
          )
      )
    )
  );
  /* -------------------------------------------------------------------------- */
  /*                                Order Details                               */
  /* -------------------------------------------------------------------------- */
  openOrderDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.openOrderDetails),
      switchMap(action =>
        this.api.getOrderDetails(action.orderId).pipe(
          map(orderDetails =>
            OrderActions.openOrderDetailsSuccess({ orderDetails })
          ),
          retry(3),
          catchError(error => {
            return of(OrderActions.openOrderDetailsFailure(error));
          })
        )
      )
    )
  );

  openOrderDetailsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrderActions.openOrderDetailsSuccess),
        tap(({ orderDetails }) => {
          if (orderDetails?.invoices?.[0]?.status === 'uncollectible') {
            this.store.dispatch(
              NotificationActions.addNotification({
                notification: {
                  style: NotificationBarType.Warning,
                  message:
                    'Your payment was not successfully processed please update here.',
                  actionType: 'open-pay-modal',
                  actionText: 'Pay Now',
                  payload: {
                    order: orderDetails
                  }
                }
              })
            );
          }
        })
      ),
    { dispatch: false }
  );

  /* -------------------------------------------------------------------------- */
  /*                               Orders History                               */
  /* -------------------------------------------------------------------------- */
  getOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.getOrders),
      switchMap(({ v }) =>
        this.api.getOrders(v).pipe(
          map(res => OrderActions.getOrdersSuccess({ orderHistory: res })),
          catchError(error => {
            return of(OrderActions.getOrdersFailure(error));
          })
        )
      )
    )
  );

  //   /* -------------------------------------------------------------------------- */
  //   /*                                Order Pay Modal                      */
  //   /* -------------------------------------------------------------------------- */

  openOrderPayModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrderActions.openOrderPayModal),
        tap(({ order }) => {
          this.modalService.create({
            nzContent: OrderPayModalComponent,
            nzComponentParams: {
              order: order
            }
          });
        })
      ),
    { dispatch: false }
  );
}
